<template>
    <div class="w-full space-y-3">
        <Head title="Expenses"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Expenses</h1>
        </div>
        <div class="grid grid-cols-2 gap-2">
            <div class="relative">
                <label  for="start-date" class="block text-sm font-medium text-gray-700">Start Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
                <input v-model="form.starts_at" name="start-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select start date">
            </div>
            <div class="relative">
                <label for="end-date" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
                <input v-model="form.ends_at" name="end-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select end date">
            </div>
        </div>
        <div class="w-full flex gap-3 justify-between">
            <div class="w-full flex gap-3">
                <span @click="emitter.emit('openExpenseSlideout')"
                        class="cursor-pointer inline-flex items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Add New Expense
                </span>
                <span @click="emitter.emit('openManageCards')"
                        class="cursor-pointer inline-flex items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <CreditCardIcon class="-ml-0.5 mr-2 h-4 w-4"/>
                    Manage Cards
                </span>
            </div>
            <div class="w-full flex justify-end justify-content-end align-items-end">
                <search-filter :show-filter="false" v-model:value="form.search" @reset="reset"></search-filter>
            </div>
        </div>
        <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6 overflow-hidden">
            <div class="text-center py-4 lg:px-4">
                <div class="p-2 bg-light-pitch-purple items-center text-white leading-none lg:rounded-md flex lg:inline-flex" role="alert">
                    <span class="flex rounded-full bg-dark-pitch-purple uppercase px-2 py-1 text-xs font-bold mr-3">
                        <InformationCircleIcon class="w-5 h-5 text-white"></InformationCircleIcon>
                    </span>
                    <span class="mr-2 text-left text-sm uppercase font-bold flex-auto text-white">Current page GBP total {{ formatCurrency(paginated_expenses_total_amount, 'GBP' , true) }}, of total GBP amount {{ formatCurrency(expenses_total_amount, 'GBP' , true) }}.</span>
                </div>
            </div>
            <div class="flex flex-col mt-3 space-y-3 flex-grow overflow-hidden">
                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="py-2 align-middle min-w-full sm:px-6 lg:px-8 inline-block" :class="{'flex flex-col h-screen overflow-hidden' : expenses.data.length > 8}">
                        <div class="table-container shadow border-b border-gray-200 sm:rounded-lg"  :class="{'flex-grow overflow-auto' : expenses.data.length > 8}">
                            <table class="min-w-full divide-y divide-gray-200" :class="{'relative w-full' : expenses.data.length > 8}">
                                <thead class="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Fixture
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Supplier
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Payment Date
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Card (last 4 digits)
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Expense Type
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Transaction Type
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Nominal
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Cost Center
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                <tr v-if="expenses.data.length > 0" v-for="(expense,index) in expenses.data"
                                    :key="index"
                                    :class="{'bg-gray-100': index % 2}"
                                    class="hover:bg-gray-50 cursor-pointer">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ decodeEntities(expense.description?.replace(/<\/?[^>]+>/ig, " ")) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <Link class="underline" v-if="expense.fixture !== null" :href="route('frontend.fixture.show', expense.fixture.id)">{{ expense.fixture.title }}</Link>
                                        <span v-if="expense.fixture_id === 0">Non Client Applicable</span>
                                        <span v-else>----</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                        v-html="expense.supplier">
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ moment(expense.paid_at, 'YYYY-MM-DD').format('DD/MM/YYYY') }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(expense.amount, expense.currency.code, true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="!expense.card || expense.card.card_number == ''">
                                            N/A
                                        </span>
                                        <span v-else>
                                            {{ expense.card.card_number.slice(-4) }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.expense_type.name }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex flex-col">
                                        {{ expense.transaction_type.name }}
                                        <span class="w-full text-gray-400">{{ expense.transaction_type.title }}</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.transaction_type.nominal }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.transaction_type.cost_center }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex">
                                        <PencilAltIcon @click="emitter.emit('editExpenseSlideout', expense)"
                                                       class="w-4 h-4 mr-3"></PencilAltIcon>
                                        <TrashIcon @click="deleteExpense(expense.id)"
                                                   class="w-4 h-4 text-red-500"></TrashIcon>
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td colspan="8"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        There are no expenses available for your search criteria.
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <div class="w-100 mt-6 text-right">
                <Pagination :links="expenses.links" />
            </div>
        </div>
    </div>
    <ManageCards/>
</template>

<script>
import Authenticated from "@/Layouts/Authenticated";
import {Link, Head, useForm} from "@inertiajs/vue3";
import {emitter} from "@/eventHub";
import {CreditCardIcon, TrashIcon, PencilAltIcon, InformationCircleIcon} from "@heroicons/vue/outline";
import ManageCards from "@/Components/SlideOuts/ManageCards";
import moment from "moment";
import { router } from '@inertiajs/vue3'
import Pagination from "@/Components/Pagination";
import debounce from "lodash/debounce";
import pickBy from "lodash/pickBy";
import SearchFilter from "@/Components/SearchFilter";
import mapValues from 'lodash/mapValues';

export default {
    layout: Authenticated,
    components: {
        ManageCards,
        Head,
        Link,
        CreditCardIcon,
        TrashIcon,
        PencilAltIcon,
        Pagination,
        SearchFilter,
        InformationCircleIcon
    },
    props: ['expenses', 'starts_at', 'ends_at', 'expenses_total_amount', 'paginated_expenses_total_amount'],
    data(props) {
        return {
            emitter,
            form: useForm({
                starts_at: props.starts_at,
                ends_at: props.ends_at
            })
        }
    },
    computed: {
        formattedStartDate() {
            if (this.form.starts_at) {
                return moment(this.form.starts_at).format('DD MMM YYYY');
            }
            return '';
        },
        formattedEndDate() {
            if (this.form.ends_at) {
                return moment(this.form.ends_at).format('DD MMM YYYY');
            }
            return '';
        }
    },
    created() {
        this.moment = moment
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        deleteExpense(id) {
            this.$swal.fire({
                title: 'Remove Expense',
                html: "You are removing an expense. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Delete the country
                        this.$inertia.delete(route('frontend.expenses.expense.delete', id), {
                            preserveScroll: true
                        });
                    }
                });
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        decodeEntities(encodedString) {
            const parser = new DOMParser();
            const dom = parser.parseFromString(`<!doctype html><body>${encodedString}`, 'text/html');
            return dom.body.textContent;
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function() {
                this.$inertia.get(route('frontend.expenses.index'), pickBy(this.form), {preserveState: true, preserveScroll: true})
            }, 500),
        }
    }
}
</script>
